// Standalone flag module (no imports) so both the snapshot importer and core
// plumbing (beforeView, sync realtime handlers) can share it without creating
// import cycles.
//
// While a snapshot import is running, the local league DB is deleted and
// recreated. Anything that touches league connections during that window - a
// view refresh, or beforeLeague closing/opening leagues on navigation - can
// yank the connection out from under the import mid-write and permanently
// corrupt the league (empty gameAttributes -> "Undefined season" forever).
let importing = false;

export const setImportingSnapshot = (value: boolean) => {
	importing = value;
};

export const isImportingSnapshot = () => importing;

// Bounded wait for any in-flight snapshot import to finish. User edits (roster
// order, playing time, finances) write to idb.cache - if an import is mid-
// recreation of the league DB, those writes land in the doomed pre-import cache
// and are silently wiped when the import's fresh cache replaces it. Edit APIs
// await this first so the write lands in the post-import state. Fast no-op in
// the common case (nothing importing) and in single-player leagues.
export const waitForImportIdle = async (timeoutMs = 60000) => {
	const start = Date.now();
	while (importing && Date.now() - start < timeoutMs) {
		await new Promise((resolve) => {
			setTimeout(resolve, 250);
		});
	}
};

// Mutual exclusion between snapshot imports and beforeLeague (the league
// page-load path). A one-way "is importing" check isn't enough: a page load
// that slips in just before the import starts holds/kills the league DB
// connection the import needs, and vice versa. Both sides run under this
// lock so they can never interleave. Single-threaded worker + promise chain =
// a simple fair FIFO mutex.
//
// Debug breadcrumbs on globalThis (readable via evalOnWorker; worker console
// output is invisible to the page for SharedWorkers): mpLockTrace records the
// last 30 acquire/release transitions with labels + timestamps, so a
// permanently-held lock names its holder.
const lockTrace = (entry: string) => {
	const trace = ((globalThis as any).mpLockTrace ?? []) as string[];
	trace.push(`${new Date().toISOString()} ${entry}`);
	(globalThis as any).mpLockTrace = trace.slice(-30);
};
let chain: Promise<unknown> = Promise.resolve();
export const withLeagueDbLock = <T>(
	fn: () => Promise<T>,
	label = "?",
): Promise<T> => {
	lockTrace(`queued ${label}`);
	const wrapped = async () => {
		lockTrace(`acquired ${label}`);
		try {
			return await fn();
		} finally {
			lockTrace(`released ${label}`);
		}
	};
	const run = chain.then(wrapped, wrapped);
	// Keep the chain alive regardless of fn's outcome
	chain = run.catch(() => {});
	return run;
};

// Self-healing hook: when a multiplayer league's local DB fails to open (a
// snapshot import interrupted mid-write - e.g. the user navigated away while
// it was recreating the database - leaves it half-created), the league can be
// rebuilt from the canonical Supabase snapshot. sync.ts registers the actual
// implementation at module load; the indirection keeps this module import-free
// so beforeView can call it without pulling in the whole multiplayer graph.
type LeagueRepairer = (lid: number) => Promise<boolean>;
let leagueRepairer: LeagueRepairer | undefined;

export const registerLeagueRepairer = (fn: LeagueRepairer) => {
	leagueRepairer = fn;
};

// Returns true if the league was rebuilt and opening should be retried
export const repairLeague = async (lid: number): Promise<boolean> => {
	return leagueRepairer ? leagueRepairer(lid) : false;
};
